import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DatePicker } from '@/components/synapse/DatePicker';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { NotesTextarea } from '@/components/synapse/NotesTextarea';
import { IssueLineEntry } from '@/features/inventory/issue-lines/IssueLineEntry';
import { IssueLineList } from '@/features/inventory/issue-lines/IssueLineList';
import { useIssueLines } from '@/features/inventory/issue-lines/useIssueLines';
import { ageFromDob, sexLabelKey } from '@/features/patients/patientDisplay';
import type { IssueEntry } from '@/types/api';
import type { OutsideMedicine, PatientSex } from '@/types/patients';
import {
  OutsideMedicinesEditor,
  newOutsideRow,
  toOutsideMedicines,
  type OutsideMedicineRow,
} from './OutsideMedicinesEditor';
import type { DispensedLine, Visit } from './useVisits';

/** The patient the visit is for — name, age and gender, nothing clinical. */
export interface VisitFormPatient {
  fullName: string;
  sex: PatientSex;
  dateOfBirth: string | null;
  mrn: string;
}

/** What the form hands back on Save. The page turns it into its API call. */
export interface VisitFormValues {
  /** Trimmed; undefined when the box is empty. */
  diagnosisNote: string | undefined;
  outsideMedicines: OutsideMedicine[];
  /** Medicines newly picked in this session. */
  added: IssueEntry[];
  /** Changes to medicines already dispensed for this visit (edit only). */
  updated: { movementId: string; quantity: number }[];
  removed: string[];
  /** `yyyy-mm-dd`, or undefined for today (new visit only). */
  visitDate: string | undefined;
}

// A new visit and a correction are different acts with different inputs: a
// correction has dispensed lines to change and no date to choose. A union, so
// each page states which it is rather than passing a visit-or-nothing.
export type VisitFormMode = { kind: 'new' } | { kind: 'edit'; visit: Visit };

interface VisitFormProps {
  mode: VisitFormMode;
  patient: VisitFormPatient;
  /** The queue number being answered, when there is one. */
  ticketNumber?: number;
  saving: boolean;
  /** A failed save, already put into words. */
  error: string | null;
  onSubmit: (values: VisitFormValues) => void;
  onCancel: () => void;
}

const DIAGNOSIS_MAX = 5000;

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The one screen where a visit is typed up from the doctor's paper: the
 * diagnosis as written, the medicines dispensed from clinic stock, and the
 * medicines to buy outside. Used for recording a new visit and for correcting a
 * recorded one.
 *
 * It submits nothing itself. Dispensing happens on the server in the same save
 * as the visit; this collects what was written and hands it to the page.
 */
export function VisitForm({
  mode,
  patient,
  ticketNumber,
  saving,
  error,
  onSubmit,
  onCancel,
}: VisitFormProps) {
  const { t } = useTranslation();
  const existing = mode.kind === 'edit' ? mode.visit : null;

  const [diagnosis, setDiagnosis] = useState(existing?.diagnosisNote ?? '');
  const [outside, setOutside] = useState<OutsideMedicineRow[]>(() =>
    (existing?.outsideMedicines ?? []).map((m) => newOutsideRow(m)),
  );
  const [visitDate, setVisitDate] = useState<string | null>(null);
  // Edits to lines already dispensed, keyed by movement id. A line absent from
  // both is unchanged.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [removed, setRemoved] = useState<Set<string>>(() => new Set());
  const lines = useIssueLines();

  const dispensed = existing?.issued ?? [];
  const kept = dispensed.filter((l) => !removed.has(l.movementId));
  const outsideMedicines = toOutsideMedicines(outside);
  const note = diagnosis.trim();

  const hasContent =
    note !== '' || outsideMedicines.length > 0 || lines.pending.length > 0 || kept.length > 0;
  // A medicine picked but not yet added would be silently left out of the save.
  const halfPicked = lines.draft !== null;
  const tooLong = note.length > DIAGNOSIS_MAX;
  const canSave = hasContent && !halfPicked && !tooLong && !saving;

  const submit = () => {
    if (!canSave) return;
    onSubmit({
      diagnosisNote: note === '' ? undefined : note,
      outsideMedicines,
      added: lines.entries,
      updated: kept
        .filter((l) => quantities[l.movementId] !== undefined && quantities[l.movementId] !== l.quantity)
        .map((l) => ({ movementId: l.movementId, quantity: quantities[l.movementId]! })),
      removed: [...removed],
      visitDate: visitDate ?? undefined,
    });
  };

  const age = ageFromDob(patient.dateOfBirth);

  return (
    <div className="mx-auto flex w-full max-w-[920px] flex-col gap-5">
      {/* Who this is for. Read-only: the form never edits the patient. */}
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-line bg-paper px-5 py-4">
        {ticketNumber !== undefined && (
          <div
            className="flex h-12 min-w-12 items-center justify-center rounded-md bg-accent px-2 font-mono text-[20px] font-bold tabular-nums text-accent-foreground"
            aria-label={t('aptCounter.numberLabel', { number: ticketNumber })}
          >
            {ticketNumber}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[20px] font-bold leading-tight tracking-[-0.02em]">
            {patient.fullName}
          </h1>
          <div className="mt-0.5 text-[13px] text-muted">
            {age != null && (
              <>
                {age} {t('patients.yrs')} ·{' '}
              </>
            )}
            {t(sexLabelKey(patient.sex))}
            {' · '}
            <span className="font-mono">{patient.mrn}</span>
          </div>
        </div>
        {mode.kind === 'new' && (
          <Field label={t('visits.visitDate')} className="w-[180px]">
            {(id) => (
              <DatePicker
                id={id}
                value={visitDate}
                onChange={setVisitDate}
                defaultToToday
                max={todayIso()}
                clearable
              />
            )}
          </Field>
        )}
      </header>

      <Card className="p-5">
        <Field
          label={t('visits.diagnosis')}
          hint={t('visits.diagnosisHint')}
          error={tooLong ? t('visits.diagnosisTooLong', { max: DIAGNOSIS_MAX }) : undefined}
        >
          {(id) => (
            <NotesTextarea
              id={id}
              value={diagnosis}
              onChange={setDiagnosis}
              placeholderKey="visits.diagnosisPlaceholder"
              minRows={5}
              maxRows={16}
            />
          )}
        </Field>
      </Card>

      <Card className="p-5">
        <SectionHeading
          title={t('visits.issuedTitle')}
          subtitle={t('visits.issuedSubtitle')}
          icon="box_out"
        />

        {dispensed.length > 0 && (
          <ul className="mb-4 flex flex-col gap-2">
            {dispensed.map((line) => (
              <li key={line.movementId}>
                <DispensedRow
                  line={line}
                  quantity={quantities[line.movementId] ?? line.quantity}
                  removed={removed.has(line.movementId)}
                  onQuantity={(q) =>
                    setQuantities((prev) => ({ ...prev, [line.movementId]: Math.max(1, q) }))
                  }
                  onToggleRemoved={() =>
                    setRemoved((prev) => {
                      const next = new Set(prev);
                      if (next.has(line.movementId)) next.delete(line.movementId);
                      else next.add(line.movementId);
                      return next;
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )}

        <IssueLineEntry lines={lines} addLabelKey="visits.addToVisit" />

        {lines.pending.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
              {t('visits.issuedToAdd')}
            </div>
            <IssueLineList lines={lines} />
          </div>
        )}
      </Card>

      <Card className="p-5">
        <SectionHeading
          title={t('visits.outsideTitle')}
          subtitle={t('visits.outsideSubtitle')}
          icon="pill"
        />
        <OutsideMedicinesEditor rows={outside} onChange={setOutside} />
      </Card>

      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-md border border-destructive bg-destructive-bg px-3 py-2.5 text-[13px] text-destructive"
        >
          <Icon name="alert" size={15} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[12.5px] text-muted">
          {halfPicked
            ? t('visits.finishPicking')
            : !hasContent
              ? t('visits.needsContent')
              : mode.kind === 'new'
                ? t('visits.saveWillRecord')
                : t('visits.saveWillAmend')}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={!canSave}>
            <Icon name="check" size={16} />
            {saving
              ? t('visits.saving')
              : mode.kind === 'new'
                ? t('visits.saveVisit')
                : t('visits.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({
  title,
  subtitle,
  icon,
}: {
  title: string;
  subtitle: string;
  icon: 'box_out' | 'pill';
}) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-2 text-muted">
        <Icon name={icon} size={18} />
      </div>
      <div>
        <h2 className="text-[14.5px] font-semibold tracking-[-0.01em]">{title}</h2>
        <p className="mt-0.5 text-[12.5px] text-muted">{subtitle}</p>
      </div>
    </div>
  );
}

interface DispensedRowProps {
  line: DispensedLine;
  quantity: number;
  removed: boolean;
  onQuantity: (q: number) => void;
  onToggleRemoved: () => void;
}

/**
 * A medicine already dispensed for this visit. Its quantity can be changed or
 * the line taken back — unless a later stock movement has touched the batch, in
 * which case the stock ledger no longer allows either and the row says so
 * instead of offering a control the save would refuse.
 */
function DispensedRow({ line, quantity, removed, onQuantity, onToggleRemoved }: DispensedRowProps) {
  const { t } = useTranslation();
  const locked = !line.editable;
  return (
    <div
      className={
        'flex flex-wrap items-center gap-3 rounded-md border border-line p-3 ' +
        (removed ? 'bg-paper-2 opacity-70' : 'bg-paper')
      }
    >
      <div className="min-w-0 flex-1">
        <div className={'flex flex-wrap items-center gap-1.5 ' + (removed ? 'line-through' : '')}>
          <span className="text-[14px] font-semibold tracking-[-0.01em]">{line.itemBrandName}</span>
          {line.itemGenericName && (
            <span className="text-[13px] text-muted">{line.itemGenericName}</span>
          )}
          {line.itemStrength && (
            <span className="font-mono text-[13px] text-ink-2 tabular-nums">· {line.itemStrength}</span>
          )}
        </div>
        <div className="mt-1 font-mono text-[11.5px] text-muted">
          {line.batchNumber ?? t('issue.batchDash')}
          {locked && <span className="ml-2 font-sans text-warn">{t('visits.lineLocked')}</span>}
          {removed && <span className="ml-2 font-sans">{t('visits.lineWillReturn')}</span>}
        </div>
      </div>

      {!removed && (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label={`${t('common.decreaseQuantity')} — ${line.itemBrandName}`}
            onClick={() => onQuantity(quantity - 1)}
            disabled={locked || quantity <= 1}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2 disabled:opacity-40"
          >
            <Icon name="minus" size={14} />
          </button>
          <span
            className="min-w-[2ch] text-center font-mono text-[15px] font-semibold tabular-nums"
            aria-label={`${t('common.quantity')} — ${line.itemBrandName}`}
          >
            {quantity}
          </span>
          <button
            type="button"
            aria-label={`${t('common.increaseQuantity')} — ${line.itemBrandName}`}
            onClick={() => onQuantity(quantity + 1)}
            disabled={locked}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2 disabled:opacity-40"
          >
            <Icon name="plus" size={14} />
          </button>
        </div>
      )}

      <Button
        type="button"
        variant={removed ? 'secondary' : 'ghost'}
        size="sm"
        onClick={onToggleRemoved}
        disabled={locked}
        aria-label={
          removed
            ? t('visits.lineKeepFor', { name: line.itemBrandName })
            : t('visits.lineRemoveFor', { name: line.itemBrandName })
        }
      >
        {removed ? t('visits.lineKeep') : t('common.remove')}
      </Button>
    </div>
  );
}
